// 1. React
import { useEffect, useState } from "react";

// 2. Routing
import { Route, Routes } from "react-router-dom";

// 3. State management
import { useDispatch } from "react-redux";

// 4. Helpers / Auth
import { classNames } from "helpers";
import { getAccessToken } from "services/auth";

// 5. Redux actions
import { fetchActiveUser } from "services/api/users/redux";
import { fetchTranslations } from "services/api/translations/redux";

// 6. Layout components
import { Menu } from "../Menu";
import Sidebar from "../Sidebar/Sidebar";
import FleetGuard from "../FleetGuard/FleetGuard";

// 7. App navigation / types
import { Routes as AppRoutes } from "../../navigation";
import { AppHomeCmp } from "./types";

// 8. Module routers
import { DashboardRouter } from "ui/modules/dashboard/navigation";
import { CalculationRouter } from "ui/modules/calculation/navigation";
import { ContactRouter } from "ui/modules/contacts/navigation";
import { PriceRouter } from "ui/modules/prices/navigation";
import { SettingRouter } from "ui/modules/settings/navigation";
import { UserRouter } from "ui/modules/users/navigation";
import { FleetVehicleRouter } from "ui/modules/fleet/vehicles/navigation";
import { FleetTireRouter } from "ui/modules/fleet/tires/navigation";
import { FleetFireExtinguisherRouter } from "ui/modules/fleet/fireExtinguishers/navigation";
import { FleetRegistrationRouter } from "ui/modules/fleet/registrations/navigation";
import { FleetCascoRouter } from "ui/modules/fleet/casco/navigation";
import { FleetTransferRouter } from "ui/modules/fleet/transfers/navigation";
import { FleetDamageRouter } from "ui/modules/fleet/damages/navigation";
import { FleetDriverRouter } from "ui/modules/fleet/drivers/navigation";
import { FleetServiceRouter } from "ui/modules/fleet/services/navigation";
import FleetWarehouseRouter from "ui/modules/fleet/warehouses/navigation/FleetWarehouseRouter";
import { fetchLanguages } from "services/api/languages/redux";
import { fetchSettings } from "services/api/settings/redux";
import FinanceRouter from "ui/modules/finance/navigation/FinanceRouter";
import ReportRouter from "ui/modules/reports/navigation/ReportRouter";
import { IntegrationRouter } from "ui/modules/integrations/navigation";
import { MarketplaceRouter } from "ui/modules/marketplace/navigation";
import { SalesRouter } from "ui/modules/sales/navigation";
import { NotificationRouter } from "ui/modules/notifications/navigation";
import AccountPage from "ui/modules/account/pages/AccountPage/AccountPage";


const SIDEBAR_STORAGE_KEY = "openSidebar";

const AppHome: AppHomeCmp = (props: any) => {
  const dispatch = useDispatch();
  const [menuHeight, setMenuHeight] = useState(0);


  const [openSidebar, setOpenSidebar] = useState<boolean>(() => {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken) {
      dispatch(fetchActiveUser() as any);
      dispatch(fetchTranslations() as any);
      dispatch(fetchLanguages() as any);
      dispatch(fetchSettings() as any);
    }

    calculateMenuHeight();
  }, [dispatch]);


  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(openSidebar));

    if (openSidebar) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [openSidebar]);

  const calculateMenuHeight = () => {
    const menu = document.getElementById("js-menu-top");
    if (menu) setMenuHeight(menu.offsetHeight);
  };

  const handleSidebarToggle = () => {
    setOpenSidebar((prev) => !prev);
  };

  return (
    <>
      <FleetGuard />

      <Menu openMenu={openSidebar} onOpenMenu={handleSidebarToggle} />

      <div
        className="flex bg-[#161719]"
        style={{ height: `calc(100vh - ${menuHeight}px)` }}
      >
        <Sidebar
          openSidebar={openSidebar}
          onOpenSidebar={handleSidebarToggle}
        />

        <div
          className={classNames(
            // Razmak oko sadržaja ima smisla dok okvir „pluta" na tamnoj podlozi; na malom ekranu
            // sadržaj ide do ruba, inače se s desne strane vidi tamna crta.
            "w-full bg-[#f9fafc] rounded mr-0 xl:mr-3 mb-[-1px] xl:mb-3 overflow-y-auto"
          )}
        >
          <Routes>
            <Route path={DashboardRouter.route} element={<DashboardRouter />} />
          </Routes>
          <Routes>
            <Route path="/account" element={<AccountPage />} />
          </Routes>
          <Routes>
            <Route
              path={FleetVehicleRouter.route}
              element={<FleetVehicleRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetTireRouter.route}
              element={<FleetTireRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetFireExtinguisherRouter.route}
              element={<FleetFireExtinguisherRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetRegistrationRouter.route}
              element={<FleetRegistrationRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetCascoRouter.route}
              element={<FleetCascoRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetTransferRouter.route}
              element={<FleetTransferRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetDamageRouter.route}
              element={<FleetDamageRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetDriverRouter.route}
              element={<FleetDriverRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetServiceRouter.route}
              element={<FleetServiceRouter />}
            />
          </Routes>
          <Routes>
            <Route
              path={FleetWarehouseRouter.route}
              element={<FleetWarehouseRouter />}
            />
          </Routes>
          <Routes>
            <Route path={FinanceRouter.route} element={<FinanceRouter />} />
          </Routes>
          <Routes>
            <Route path={ReportRouter.route} element={<ReportRouter />} />
          </Routes>
          <Routes>
            <Route path={ContactRouter.route} element={<ContactRouter />} />
          </Routes>

          <Routes>
            <Route path={UserRouter.route} element={<UserRouter />} />
          </Routes>

          <Routes>
            <Route path={SettingRouter.route} element={<SettingRouter />} />
          </Routes>

          <Routes>
            <Route
              path={CalculationRouter.route}
              element={<CalculationRouter />}
            />
          </Routes>
          <Routes>
            <Route path={PriceRouter.route} element={<PriceRouter />} />
          </Routes>
          <Routes>
            <Route path={IntegrationRouter.route} element={<IntegrationRouter />} />
          </Routes>
          <Routes>
            <Route path={NotificationRouter.route} element={<NotificationRouter />} />
          </Routes>
          <Routes>
            <Route path={MarketplaceRouter.route} element={<MarketplaceRouter />} />
          </Routes>
          <Routes>
            <Route path={SalesRouter.route} element={<SalesRouter />} />
          </Routes>
        </div>
      </div>
    </>
  );
};

AppHome.route = AppRoutes.AppHome;

export default AppHome;
